"use client";

import type { Signature } from "@/lib/studio/studio";

/**
 * Catalog of pickable time signatures, with an optional italic mood hint.
 * Kept inside the picker because it's purely a UI concern, the underlying
 * metronome runtime accepts any `{num, den}`.
 */
type SignatureOption = Signature & {
  readonly label: string;
  readonly hint?: string;
};

const SIGNATURE_OPTIONS: readonly SignatureOption[] = [
  { num: 2, den: 4, label: "2/4", hint: "Marche" },
  { num: 3, den: 4, label: "3/4", hint: "Valse" },
  { num: 4, den: 4, label: "4/4", hint: "Standard" },
  { num: 5, den: 4, label: "5/4", hint: "Take Five" },
  { num: 6, den: 8, label: "6/8", hint: "Berceuse" },
  { num: 7, den: 8, label: "7/8", hint: "Balkan" },
  { num: 9, den: 8, label: "9/8" },
  { num: 12, den: 8, label: "12/8", hint: "Shuffle" },
];

interface MetronomeSignaturePickerProps {
  value: Signature;
  onChange: (s: Signature) => void;
  /** When true the grid is non-interactive, used when a piece dictates the signature. */
  locked?: boolean;
}

function isSameSignature(a: Signature, b: Signature): boolean {
  return a.num === b.num && a.den === b.den;
}

export function MetronomeSignaturePicker({
  value,
  onChange,
  locked = false,
}: MetronomeSignaturePickerProps) {
  return (
    <div
      className={`grid grid-cols-4 gap-1.5 ${
        locked ? "opacity-[0.55] pointer-events-none" : ""
      }`}
      role="radiogroup"
      aria-label="Signature rythmique"
    >
      {SIGNATURE_OPTIONS.map((s) => {
        const active = isSameSignature(value, s);

        return (
          <button
            key={s.label}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange({ num: s.num, den: s.den })}
            disabled={locked}
            className={[
              "flex flex-col items-center gap-0.5 px-1.5 py-2 rounded-md border transition-colors duration-150",
              active
                ? "border-[var(--color-violet-glow)] bg-[var(--color-violet-soft)]"
                : "border-border bg-bg-deep hover:border-border-subtle",
              locked ? "cursor-default" : "cursor-pointer",
            ].join(" ")}
            style={
              active
                ? { boxShadow: "0 0 12px var(--color-violet-glow)" }
                : undefined
            }
          >
            <span
              className={`font-mono text-[13px] leading-none font-medium ${
                active ? "text-text-primary" : "text-text-secondary"
              }`}
            >
              {s.label}
            </span>
            <span className="text-[9px] italic text-text-muted h-2.5 leading-none">
              {s.hint ?? ""}
            </span>
          </button>
        );
      })}
    </div>
  );
}
